import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  getPondsKey,
  getFarmOverviewKey,
  POND_TYPES_KEY,
  SHRIMP_BRANDS_KEY,
  getCycleKey,
  getCyclesKey,
  getPondDashboardKey,
  getPondInfoKey,
  getShrimpTypesKey,
  SPECIES_KEY,
  getBreedKey,
  getBreedBrandKey,
  getBreedProductKey,
  getBreedNameKey,
  getFeedProgramIdKey,
  getSpeciesIdKey,
  getMarketIdKey,
  AREA_UNIT,
  getPondDashboardTableKey,
  getPondMediaKey,
  getCycleSummaryKey,
  getDiseaseDashboardLabTableKey,
  getShrimpSizeTableKey,
  getPartialDateListKey,
} from 'common/constants/queryKeys'
import { STALE_TIME } from 'common/constants/staleTime'
import useCycleApi from 'common/hooks/useCycleApi'
import useFarmApi from 'common/hooks/useFarmApi'
import usePondApi from 'common/hooks/usePondApi'
import { BasePaginationResponseV3, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { createFarmApi } from 'core/api/apiFactory'
import { useParams, useSearchParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import {
  CYCLE_REQUEST_CONFIG,
  DASHBOARD_REQUEST_CONFIG,
  getBreed,
  getBreedBrand,
  getBreedProduct,
  getFeedPrograms,
  getFeedProgramsById,
  getFoodBrands,
  getMarkets,
  getMarketsById,
  getMedicine,
  getPartialDateList,
  getPondAreaUnits,
  getPondTypes,
  getShrimpBrands,
  getShrimpTypes,
  getSpecies,
  getWaterColor,
  getWaterQualityThreshold,
  POND_REQUEST_CONFIG,
  DASHBOARD_REQUEST_CONFIG_LAB_TABLE,
  DASHBOARD_REQUEST_CONFIG_SHRIMP_SIZE_TABLE,
  MEDIA_ALBUM_REQUEST_CONFIG,
} from '.'
import { pondMediaFilterState, selectedCycleIdState } from '../stores/atom'
import { useSpeciesId } from './selector'
import {
  Cycle,
  CycleHistory,
  CycleSummary,
  DashboardResponse,
  FarmOverviewResponse,
  PondAgeFilter,
  PondDashboard,
  PondInfo,
  PondResponse,
  UseGetPondsOptions,
  DiseaseLabTableResponse,
  ShrimpSizeTableResponse,
  Media,
} from './types'

const ROWS_PER_PAGE = 10

export const useGetPonds = ({
  farmId: farmIdArg,
  useArg = false,
  refetchOnMount,
}: UseGetPondsOptions = {}) => {
  const { farmId: farmIdParam } = useParams<{ farmId: string }>()
  const farmId = useArg ? farmIdArg : farmIdParam

  return useQuery<PondResponse[]>({
    queryKey: getPondsKey(farmId),
    queryFn: async () => {
      if (!farmId) return []

      const { data } = await createFarmApi(farmId!).request<BaseResponse<PondResponse[]>>(
        POND_REQUEST_CONFIG.getPonds
      )
      return data.data
    },
    enabled: !!farmId,
    staleTime: STALE_TIME,
    refetchOnMount,
  })
}

/**
 * The อายุกุ้ง filter accepts decimals, the endpoint whole days ≥ 0. Rounding inward keeps the
 * same ponds the grid keeps: age is a whole number, so `age >= 30.5` is `age >= 31`.
 */
const toAgeParam = (value: number | null, round: (value: number) => number) =>
  value === null ? undefined : Math.max(0, round(value))

/**
 * The ภาพรวมฟาร์ม summary row (NK-296). Its own request, so the pond grid renders as soon as
 * GET /ponds lands instead of waiting on the slower ผลผลิตรวม math behind these averages.
 */
export const useGetFarmOverview = ({
  ageFilter,
  enabled = true,
}: {
  ageFilter: PondAgeFilter
  enabled?: boolean
}) => {
  const { farmId } = useParams<{ farmId: string }>()
  const minAge = toAgeParam(ageFilter.min, Math.ceil)
  const maxAge = toAgeParam(ageFilter.max, Math.floor)

  return useQuery<FarmOverviewResponse>({
    queryKey: getFarmOverviewKey(farmId, minAge, maxAge),
    queryFn: async () => {
      const { data } = await createFarmApi(farmId!).request<BaseResponse<FarmOverviewResponse>>({
        ...POND_REQUEST_CONFIG.getFarmOverview,
        params: { minAge, maxAge },
      })
      return data.data
    },
    enabled: enabled && !!farmId,
    staleTime: STALE_TIME,
    refetchOnMount: 'always',
  })
}

export const useGetPondTypes = () => {
  return useQuery({
    queryKey: POND_TYPES_KEY,
    queryFn: async () => {
      const { data } = await getPondTypes()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetShrimpBrands = () => {
  return useQuery({
    queryKey: SHRIMP_BRANDS_KEY,
    queryFn: async () => {
      const { data } = await getShrimpBrands()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetShrimpTypes = (shrimpBrand: number) => {
  return useQuery({
    queryKey: getShrimpTypesKey(shrimpBrand),
    queryFn: async () => {
      const { data } = await getShrimpTypes(shrimpBrand)
      return data.data
    },
    staleTime: Infinity,
    enabled: shrimpBrand !== -1,
  })
}

export const useGetCycles = () => {
  const pondApi = usePondApi()
  const { pondId } = useParams()

  return useQuery({
    queryKey: getCyclesKey(pondId),
    queryFn: async () => {
      const { data } = await pondApi.request<BaseResponse<CycleHistory[]>>(
        CYCLE_REQUEST_CONFIG.getCycles
      )
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetCycleById = () => {
  const pondApi = usePondApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getCycleKey(cycleId || ''),
    queryFn: async () => {
      const { data } = await pondApi.request<BaseResponse<Cycle>>({
        ...CYCLE_REQUEST_CONFIG.getCycleById,
        url: `${CYCLE_REQUEST_CONFIG.getCycleById.url}/${cycleId}`,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

/**
 * The selected cycle's dashboard. `enabled: false` lets a caller that only sometimes needs it
 * (the pond header's บ่อลูก badge on a past cycle) share the cached query without forcing a
 * fetch on every tab.
 */
export const useGetPondDashboard = (options?: { enabled?: boolean }) => {
  const pondApi = usePondApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId } = useParams()

  return useQuery({
    queryKey: getPondDashboardKey(pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data: cycle } = await pondApi.request<BaseResponse<Cycle>>({
        ...CYCLE_REQUEST_CONFIG.getCycleById,
        url: `${CYCLE_REQUEST_CONFIG.getCycleById.url}/${cycleId}`,
      })
      const { data: pondDashboard } = await pondApi.request<BaseResponse<PondDashboard>>({
        ...DASHBOARD_REQUEST_CONFIG.getDashboard,
        url: `/cycles/${cycleId}${DASHBOARD_REQUEST_CONFIG.getDashboard.url}`,
      })
      return {
        ...pondDashboard.data,
        status: cycle?.data?.status,
        breedId: cycle?.data?.breedProduct.breedId,
      }
    },
    enabled: !!cycleId && (options?.enabled ?? true),
  })
}

export const useGetPondInfo = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getPondInfoKey(cycleId ?? ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<PondInfo>>(
        DASHBOARD_REQUEST_CONFIG.getPondDasboard
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetCycleSummary = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getCycleSummaryKey(cycleId ?? ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CycleSummary>>(
        CYCLE_REQUEST_CONFIG.getCycleSummary
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetRevampPondDashboardPagination = () => {
  const [searchParams] = useSearchParams()
  const cycleApi = useCycleApi()
  const cycleIdState = useRecoilValue(selectedCycleIdState)
  const cycleIdParams = searchParams.get('cycleId')
  const isMobile = searchParams.get('isMobile') == 'true'
  let cycleIdToUse = cycleIdState

  if (isMobile && cycleIdParams) {
    cycleIdToUse = cycleIdParams
  }

  const initParams = {
    params: {
      page: 1,
    },
  }

  return useInfiniteQuery({
    queryKey: getPondDashboardTableKey(cycleIdToUse ?? '', { page: initParams.params.page }),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BaseResponse<DashboardResponse>>({
        ...DASHBOARD_REQUEST_CONFIG.getRevampPondDashboard,
        ...pageParam,
      })
      const curPage = data.data.pagination.current_page
      const totalPage = data.data.pagination.total_pages
      let nextPage: number | null = curPage + 1
      if (curPage === totalPage) {
        nextPage = null
      }

      return {
        data: data.data,
        nextPage: nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return {
          params: { page: lastPage.nextPage },
        }
      }
    },
    staleTime: Infinity,
    enabled: !!cycleIdToUse,
    refetchOnMount: 'always',
  })
}

export const useGetSpecies = () => {
  return useQuery({
    queryKey: SPECIES_KEY,
    queryFn: async () => {
      const { data } = await getSpecies()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetBreed = (speciesId?: number) => {
  return useQuery({
    queryKey: getBreedKey(speciesId ?? 0),
    queryFn: async () => {
      const { data } = await getBreed(speciesId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!speciesId,
  })
}

export const useGetBreedName = (breedId?: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedNameKey(speciesId, breedId ?? 0),
    queryFn: async () => {
      if (!speciesId || !breedId) return ''
      const { data } = await getBreed(speciesId)
      const breed = data?.data?.find((element: { id: number }) => element.id === breedId)

      return breed ? breed.name : ''
    },
    staleTime: Infinity,
    enabled: !!speciesId && !!breedId,
  })
}

export const useGetBreedBrand = (breedId: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedBrandKey(breedId),
    queryFn: async () => {
      const { data } = await getBreedBrand({ speciesId, breedId })
      return data.data
    },
    staleTime: Infinity,
    enabled: breedId > 0,
  })
}

export const useGetBreedProduct = (breedId: number, breedBrandId: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedProductKey(breedId, breedBrandId),
    queryFn: async () => {
      const { data } = await getBreedProduct({ speciesId, breedId, breedBrandId })
      return data.data
    },
    staleTime: Infinity,
    enabled: breedBrandId > 0,
  })
}

export const useGetFeedPrograms = () => {
  return useQuery({
    queryFn: async () => {
      const { data } = await getFeedPrograms()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetFeedProgramsById = (feedProgramId?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: getFeedProgramIdKey(feedProgramId ?? 0),
    queryFn: async () => {
      const { data } = await getFeedProgramsById(feedProgramId)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!feedProgramId && !!enabled,
  })
}

export const useGetMarkets = (speciesId?: number) => {
  return useQuery({
    queryKey: getSpeciesIdKey(speciesId ?? 0),
    queryFn: async () => {
      const { data } = await getMarkets(speciesId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!speciesId,
  })
}

export const useGetMarketsById = (marketId?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: getMarketIdKey(marketId ?? 0),
    queryFn: async () => {
      const { data } = await getMarketsById(marketId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!marketId && !!enabled,
  })
}

export const useGetPondAreaUnits = () => {
  return useQuery({
    queryKey: AREA_UNIT,
    queryFn: async () => {
      const {
        data: { data },
      } = await getPondAreaUnits()

      return data
    },
    staleTime: Infinity,
  })
}

export const useGetPondMedia = () => {
  const pondApi = usePondApi()
  const { pondId } = useParams()
  const { date, sort, tag } = useRecoilValue(pondMediaFilterState)
  const fmtDate = date ? formatDate(date) : ''
  const initParams = {
    page: 1,
    size: ROWS_PER_PAGE,
    date: fmtDate,
    sort,
    tag,
  }

  return useInfiniteQuery({
    queryKey: getPondMediaKey(pondId ?? '', fmtDate, sort, tag),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await pondApi.request<BasePaginationResponseV3<Media[]>>({
        ...MEDIA_ALBUM_REQUEST_CONFIG.getPondMedia,
        params: { ...pageParam },
      })

      return {
        medias: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return { ...initParams, page: lastPage.nextPage }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
  })
}

export const useGetDashboardBootstrap = () => {
  const [searchParams] = useSearchParams()
  const cycleIdState = useRecoilValue(selectedCycleIdState)
  const isMobile = searchParams.get('isMobile') == 'true'
  const cycleIdParams = searchParams.get('cycleId')
  const cycleIdToUse = isMobile && cycleIdParams ? cycleIdParams : cycleIdState

  return useQuery({
    queryKey: ['POND_DASHBOARD_BOOTSTRAP', cycleIdToUse],
    queryFn: async () => {
      const [
        medicineRes,
        vitaminRes,
        feedRes,
        foodBrandsRes,
        waterColorRes,
        waterQualityThresholdRes,
      ] = await Promise.all([
        getMedicine('medicine'),
        getMedicine('vitamin'),
        getMedicine('feed'),
        getFoodBrands(),
        getWaterColor(),
        getWaterQualityThreshold(),
      ])

      return {
        medicine: medicineRes.data.data,
        vitamin: vitaminRes.data.data,
        feed: feedRes.data.data,
        foodBrands: foodBrandsRes.data.data,
        waterColor: waterColorRes.data.data,
        waterQualityThreshold: waterQualityThresholdRes.data.data,
      }
    },
    staleTime: Infinity,
    enabled: !!cycleIdToUse,
  })
}

export const useGetPartialDateList = () => {
  const cycleIdState = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getPartialDateListKey(cycleIdState || ''),
    queryFn: async () => {
      const { data } = await getPartialDateList(cycleIdState || '')
      return data.data
    },
    staleTime: STALE_TIME,
    enabled: !!cycleIdState,
  })
}

// NK-276: paginated pages of the all-ponds ไซส์กุ้ง matrix. Each page is a window
// of dates, newest first; the selector merges them into one column axis.
export const useShrimpSizeTablePagination = () => {
  const farmApi = useFarmApi()
  const { farmId } = useParams()

  return useInfiniteQuery({
    queryKey: getShrimpSizeTableKey(farmId ?? '', { page: 1 }),
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await farmApi.request<BaseResponse<ShrimpSizeTableResponse>>({
        ...DASHBOARD_REQUEST_CONFIG_SHRIMP_SIZE_TABLE.getShrimpSizeTable,
        params: { page: pageParam },
      })

      return {
        data: data.data,
        nextPage:
          data.data.pagination.current_page < data.data.pagination.total_pages
            ? data.data.pagination.current_page + 1
            : undefined,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!farmId,
    staleTime: Infinity,
    refetchOnMount: 'always',
    // NK-305: the mobile app keeps this page alive in a farm tab while the user
    // edits a pond, and fires `focus` into it on the way back; refetch then, so
    // the table shows what was just recorded on the pond page.
    refetchOnWindowFocus: 'always',
  })
}

export const useDiseaseDashboardLabTablePagination = () => {
  const farmApi = useFarmApi()
  const { farmId } = useParams()

  return useInfiniteQuery({
    queryKey: getDiseaseDashboardLabTableKey(farmId ?? '', { page: 1 }),
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await farmApi.request<BaseResponse<DiseaseLabTableResponse>>({
        ...DASHBOARD_REQUEST_CONFIG_LAB_TABLE.getLabTable,
        params: { page: pageParam },
      })

      return {
        data: data.data,
        nextPage:
          data.data.pagination.current_page < data.data.pagination.total_pages
            ? data.data.pagination.current_page + 1
            : undefined,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!farmId,
    staleTime: Infinity,
    refetchOnMount: 'always',
    // NK-305: the mobile app keeps this page alive in a farm tab while the user
    // edits a pond, and fires `focus` into it on the way back; refetch then, so
    // the table shows what was just recorded on the pond page.
    refetchOnWindowFocus: 'always',
  })
}
